import React from 'react'
import styles from './styles.module.scss'

interface SecurePortalProps {
    onScrollToForm: () => void
}

export const SecurePortal: React.FC<SecurePortalProps> = ({ onScrollToForm }) => {
    return (
        <section className={styles.portalSection}>
            <div className={styles.container}>
                <div className={styles.sectionHeader}>
                    <h2>Secure Evidence Collection Portal</h2>
                    <p>Connect your financial data securely to build the strongest possible case</p>
                </div>
                <div className={styles.portalContent}>
                    <div className={styles.portalIntro}>
                        <h3>Why Bank Statements & Credit Reports Are Critical</h3>
                        <p className={styles.leadText}>
                            Your bank statements and credit reports provide the complete financial picture that proves
                            whether your lender conducted proper affordability assessments. This evidence is often the
                            key to winning your irresponsible lending claim.
                        </p>
                    </div>

                    <div className={styles.evidenceGrid}>
                        <div className={styles.evidenceCard}>
                            <div className={styles.evidenceIcon}>
                                <span>🏦</span>
                            </div>
                            <h4>Bank Statements Show</h4>
                            <ul>
                                <li>Your actual income patterns</li>
                                <li>Regular monthly expenses</li>
                                <li>Existing financial commitments</li>
                                <li>Signs of financial stress</li>
                            </ul>
                        </div>
                        <div className={styles.evidenceCard}>
                            <div className={styles.evidenceIcon}>
                                <span>📊</span>
                            </div>
                            <h4>Credit Reports Reveal</h4>
                            <ul>
                                <li>Your borrowing history</li>
                                <li>Previous defaults or arrears</li>
                                <li>Multiple credit applications</li>
                                <li>Overall credit utilization</li>
                            </ul>
                        </div>
                    </div>

                    <div className={styles.portalFeatures}>
                        <h3>Sentinel's Free Secure Portal</h3>
                        <div className={styles.featureHighlight}>
                            <div className={styles.highlightContent}>
                                <div className={styles.highlightIcon}>🔒</div>
                                <div className={styles.highlightText}>
                                    <h4>Connect Your Data in Just a Few Clicks</h4>
                                    <p>
                                        Our secure, bank-level encrypted portal allows you to safely connect your bank
                                        accounts and access your credit report instantly. No need to download statements
                                        or wait for credit reports - we'll gather everything needed for your case
                                        automatically.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={styles.securityFeatures}>
                            <div className={styles.securityFeature}>
                                <span className={styles.checkIcon}>✓</span>
                                <span>Bank-level encryption</span>
                            </div>
                            <div className={styles.securityFeature}>
                                <span className={styles.checkIcon}>✓</span>
                                <span>FCA regulated technology</span>
                            </div>
                            <div className={styles.securityFeature}>
                                <span className={styles.checkIcon}>✓</span>
                                <span>Read-only access - we can't move money</span>
                            </div>
                            <div className={styles.securityFeature}>
                                <span className={styles.checkIcon}>✓</span>
                                <span>Instantly revocable permissions</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.evidenceImportance}>
                        <div className={styles.importanceCallout}>
                            <h4>🎯 The Full Picture Matters</h4>
                            <p>
                                Lenders are required to assess your complete financial situation before approving
                                credit. When they fail to properly review your bank statements and credit history, they
                                may have lent to you irresponsibly. Our secure portal ensures we have all the evidence
                                needed to prove this and maximize your compensation.
                            </p>
                        </div>
                    </div>
                </div>
                <div className={styles.ctaCenter}>
                    <button className={styles.ctaButton} onClick={onScrollToForm}>
                        Start Your Secure Assessment
                    </button>
                </div>
            </div>
        </section>
    )
}
